/**
 * =============================================================================
 * ADMIN PROMPT - AI-POWERED CODE AUTOMATION
 * =============================================================================
 *
 * PORTFOLIO SHOWCASE: Natural Language → Code Changes → Auto-Deploy
 *
 * This component demonstrates a complete AI automation pipeline:
 *
 * FLOW:
 * 1. User submits natural language request (with voice support)
 * 2. Request saved to Firestore → triggers Cloud Function
 * 3. Claude AI analyzes request and generates code changes
 * 4. Changes auto-committed to 'staging' branch on GitHub
 * 5. GitHub Actions auto-deploys to staging environment
 * 6. User tests changes on staging
 * 7. User clicks "Release to Prod" → merges to master → auto-deploy
 *
 * ARCHITECTURE:
 * ┌─────────────┐    ┌──────────────┐    ┌─────────────┐
 * │ AdminPrompt │───▶│ Cloud Func   │───▶│ Claude AI   │
 * │ (React)     │    │ (Firebase)   │    │ (Anthropic) │
 * └─────────────┘    └──────────────┘    └─────────────┘
 *        │                  │                   │
 *        ▼                  ▼                   ▼
 * ┌─────────────┐    ┌──────────────┐    ┌─────────────┐
 * │ Firestore   │◀───│ GitHub API   │◀───│ Code Gen    │
 * │ (Status)    │    │ (Commit)     │    │ (Changes)   │
 * └─────────────┘    └──────────────┘    └─────────────┘
 *
 * REUSABILITY:
 * This AI automation pattern can be adapted for:
 * - CMS systems: "Add a new blog post about X"
 * - E-commerce: "Create a 20% discount for category Y"
 * - Documentation: "Update the API docs for endpoint Z"
 * - Any system needing controlled, audited AI-assisted changes
 *
 * SAFETY FEATURES:
 * - Staging-first deployment (test before production)
 * - All changes logged in Firestore (audit trail)
 * - Size validation prevents AI from deleting code
 * - Human approval required for production release
 *
 * =============================================================================
 */

import React, { useState, useEffect } from 'react';
import {
  getFirestore,
  collection,
  addDoc,
  onSnapshot,
} from 'firebase/firestore';
import app from '../firebase/config';
import './AdminPrompt.css';

const db = getFirestore(app);
const ADMIN_EMAILS = [
  'mario@google.com',
  'memo@google.com',
  'mvc757@gmail.com',
];

// Cloud Function URL
const RELEASE_PROD_URL =
  'https://us-central1-muso-ninjas-80a45.cloudfunctions.net/releaseToProduction';

function AdminPrompt({ user }) {
  const [isOpen, setIsOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [prompts, setPrompts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [releasing, setReleasing] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);

  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    setSpeechSupported(!!SpeechRecognition);
  }, []);

  useEffect(() => {
    const handleOpenPanel = () => setIsOpen(true);
    window.addEventListener('openAdminPanel', handleOpenPanel);
    return () => window.removeEventListener('openAdminPanel', handleOpenPanel);
  }, []);

  const startListening = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onstart = () => setIsListening(true);
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setPrompt((prev) => (prev ? prev + ' ' + transcript : transcript));
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognition.start();
  };

  const isAdmin = user && ADMIN_EMAILS.includes(user.email?.toLowerCase());

  // Watch for status changes and auto-close dialog when committed
  useEffect(() => {
    if (!isAdmin || !db || !user) return;

    const promptsRef = collection(db, 'admin-prompts');
    const unsubscribe = onSnapshot(
      promptsRef,
      (snapshot) => {
        const promptList = [];
        snapshot.forEach((docSnap) => {
          const data = { id: docSnap.id, ...docSnap.data() };
          promptList.push(data);

          // Auto-close dialog when committed - staging auto-deploys via GitHub Actions
          if (
            data.status === 'committed' &&
            data.createdBy === user.email &&
            isOpen
          ) {
            setIsOpen(false);
            setTimeout(() => {
              alert(
                '✅ Changes committed!\n\n⏳ Auto-deploying to staging (~1-2 min)...\n\nRefresh the page in a minute to see changes.'
              );
            }, 300);
          }
        });
        promptList.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setPrompts(promptList);
      },
      (err) => {
        // Only log non-permission errors (permission errors are expected if user logs out)
        if (err.code !== 'permission-denied') {
          console.warn('AdminPrompt listener error:', err);
        }
      }
    );

    return () => unsubscribe();
  }, [isAdmin, user?.email, user, isOpen]);

  if (!isAdmin) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!prompt.trim() || !db) return;

    setSubmitting(true);
    try {
      await addDoc(collection(db, 'admin-prompts'), {
        prompt: prompt.trim(),
        status: 'pending',
        createdAt: new Date().toISOString(),
        createdBy: user.email,
        response: null,
      });
      setPrompt('');
    } catch (error) {
      alert('Failed to submit: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRelease = async (promptId) => {
    if (
      !window.confirm(
        '🚀 Release to PRODUCTION?\n\nThis will make changes live for all users.'
      )
    ) {
      return;
    }

    setReleasing(promptId);
    try {
      const response = await fetch(RELEASE_PROD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ promptId }),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Release failed');
      }

      alert(
        '🚀 Released to Production!\n\nChanges are now live at:\nhttps://muso-ninjas-80a45.web.app'
      );
    } catch (error) {
      alert('Release failed: ' + error.message);
    } finally {
      setReleasing(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { emoji: '⏳', label: 'Pending', color: '#ffc107' },
      processing: { emoji: '⚙️', label: 'Processing', color: '#17a2b8' },
      committed: { emoji: '✅', label: 'Ready', color: '#4caf50' },
      staged: { emoji: '🧪', label: 'Staged', color: '#9c27b0' },
      released: { emoji: '🚀', label: 'Live', color: '#e91e63' },
      completed: { emoji: '✅', label: 'Done', color: '#28a745' },
      error: { emoji: '❌', label: 'Error', color: '#dc3545' },
    };
    return badges[status] || badges.pending;
  };

  return (
    <>
      <button
        className="admin-prompt-fab"
        onClick={() => setIsOpen(true)}
        title="Admin Prompts"
      >
        🛠️
      </button>

      {isOpen && (
        <div className="admin-prompt-overlay" onClick={() => setIsOpen(false)}>
          <div
            className="admin-prompt-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="admin-prompt-header">
              <h2>🛠️ Admin Prompts</h2>
              <button
                className="admin-prompt-close"
                onClick={() => setIsOpen(false)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="admin-prompt-form">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe what you want to change..."
                rows={3}
                disabled={submitting || isListening}
              />
              <div className="admin-prompt-buttons">
                {speechSupported && (
                  <button
                    type="button"
                    onClick={startListening}
                    disabled={submitting || isListening}
                    className={`voice-btn ${isListening ? 'listening' : ''}`}
                  >
                    {isListening ? '🎤 Listening...' : '🎤 Voice'}
                  </button>
                )}
                <button type="submit" disabled={submitting || !prompt.trim()}>
                  {submitting ? 'Submitting...' : '📤 Submit'}
                </button>
              </div>
            </form>

            {/* Info Banner */}
            <div className="admin-prompt-info">
              <strong>💡 Tip:</strong> Changes apply here instantly. Click
              "Release to Production" when ready to go live.
            </div>

            <div className="admin-prompt-history">
              <h3>Recent Prompts</h3>
              {prompts.length === 0 ? (
                <p className="admin-prompt-empty">No prompts yet</p>
              ) : (
                <div className="admin-prompt-list">
                  {prompts.slice(0, 10).map((p) => {
                    const badge = getStatusBadge(p.status);
                    return (
                      <div
                        key={p.id}
                        className={`admin-prompt-item status-${p.status}`}
                      >
                        <div className="admin-prompt-item-header">
                          <span
                            className="admin-prompt-status"
                            style={{ background: badge.color }}
                          >
                            {badge.emoji} {badge.label}
                          </span>
                          <span className="admin-prompt-time">
                            {new Date(p.createdAt).toLocaleString()}
                          </span>
                        </div>
                        <p className="admin-prompt-text">{p.prompt}</p>
                        {p.response && (
                          <div className="admin-prompt-response">
                            {p.response}
                          </div>
                        )}

                        {/* Release to Production Button */}
                        {(p.status === 'committed' || p.status === 'staged') &&
                          p.canRelease && (
                            <button
                              className="release-btn"
                              onClick={() => handleRelease(p.id)}
                              disabled={releasing === p.id}
                            >
                              {releasing === p.id
                                ? '🔄 Releasing...'
                                : '🚀 Release to Production'}
                            </button>
                          )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AdminPrompt;
